import nr from '@/lib/network_requests.ts';
import {
  IIssue,
  IEpic,
  ISprint,
  IProject,
  IUser,
  ILogwork,
  ICustomer,
  IIssueType,
  ILogworkData,
  ILogworkWithProject,
  IAttachment,
  IComment,
  ILogworkAmountMember,
} from '@/lib/interfaces';

const queryString = (query: any) => {
  if (query) {
    return Object.keys(query)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(query[key]))
      .join('&');
  } else {
    return '';
  }
};

/**
 * Вернуть всех пользователей
 * @param  {object} query query объект
 */
export function getAllUsers(query?: Partial<IUser>): Promise<IUser[]> {
  return nr.get(`/v1/users?${queryString(query)}`, 'users.json', query);
}

/**
 * Вернуть пользователей с указанными ids
 * @param userIds ids пользователей
 */
export async function getUsersWithIds(userIds: string[], asHash?: false): Promise<IUser[]>;
export async function getUsersWithIds(userIds: string[], asHash: true): Promise<Map<IUser['_id'], IUser>>;
export async function getUsersWithIds(
  userIds: string[],
  asHash: boolean = false,
): Promise<IUser[] | Map<IUser['_id'], IUser>> {
  const MAX_USER_REQUESTS = 10;
  let userResponse: IUser[] = [];
  if (userIds.length <= MAX_USER_REQUESTS) {
    userResponse = await Promise.all(userIds.map((userId) => getUser(userId)));
  } else {
    userResponse = await getAllUsers();
  }
  if (asHash) {
    const res: Map<IUser['_id'], IUser> = new Map();
    userResponse.forEach((user) => {
      res.set(user._id!, user);
    });
    return res;
  }
  return userResponse;
}

/**
 * Вернуть спринты с указанными ids
 * @param userIds ids пользователей
 */
export async function getSprintsWithIds(projectId: string, sprintIds: string[], asHash?: false): Promise<ISprint[]>;
export async function getSprintsWithIds(
  projectId: string,
  sprintIds: string[],
  asHash: true,
): Promise<Map<ISprint['_id'], ISprint>>;
export async function getSprintsWithIds(
  projectId: string,
  sprintIds: string[],
  asHash: boolean = false,
): Promise<ISprint[] | Map<ISprint['_id'], ISprint>> {
  const MAX_USER_REQUESTS = 10;
  let sprintResponse: ISprint[] = [];
  if (sprintIds.length <= MAX_USER_REQUESTS) {
    sprintResponse = await Promise.all(sprintIds.map((sprintId) => getSprintInProject(projectId, sprintId)));
  } else {
    sprintResponse = await getAllSprints(projectId);
  }
  if (asHash) {
    const res: Map<ISprint['_id'], ISprint> = new Map();
    sprintResponse.forEach((sprint) => {
      res.set(sprint._id!, sprint);
    });
    return res;
  }
  return sprintResponse;
}

/**
 * Вернуть все проекты
 * @param  {object} query query объект
 */
export function getAllProjects(query?: object): Promise<IProject[]> {
  return nr.get(`/v1/projects?${queryString(query)}`, 'projects.json', query);
}

/**
 * Вернуть все задачи
 * @param  {object} query query объект
 */
export function getAllIssues(query?: object): Promise<IIssue[]> {
  return nr.get(`/v1/issues?${queryString(query)}`, 'issues.json', query);
}

/**
 * Вернуть все мои задачи
 * @param  {object} query query объект
 */
export function getMyIssues(query?: object): Promise<IIssue[]> {
  return nr.get(`/v1/issues/my?${queryString(query)}`, 'issues.json', query);
}

/**
 * Вернуть все задачи в активных спринтах
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getAllIssuesInActiveSprints(projectId: string, query?: object) {
  return nr.get(`/v1/issues/active_sprints/${projectId}?${queryString(query)}`, 'active_sprints.json', query);
}

/**
 * Вернуть проект с указанным id
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getProject(projectId: string, full?: true): Promise<IProject>;
export function getProject(projectId: string, full: false): Promise<IProject<false>>;
export function getProject(projectId: string, full: boolean = true): Promise<IProject | IProject<false>> {
  return nr.get(`/v1/projects/${projectId}`, 'projects.json', { _id: projectId }, 'object').then((project) => {
    if (!full) {
      project.customer = project.customer._id;
    }
    return project;
  });
}

/**
 * Создать project
 * @param {IIssue} issue Новый Issue
 */
export function createProject(project: IProject<false>) {
  return nr.post('/v1/projects', project);
}

/**
 * Обновить project
 * @param {IIssue} issue Новый Issue
 */
export function updateProject(project: IProject<false>) {
  return nr.put('/v1/projects', project);
}

/**
 * Вернуть заказчика с указанным id
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getCustomer(customerId: string): Promise<ICustomer> {
  return nr.get(`/v1/customers/${customerId}`, 'customers.json', { _id: customerId }, 'object');
}

/**
 * Создать заказчика
 * @param {IIssue} issue Новый заказчик
 */
export function createCustomer(customer: ICustomer) {
  return nr.post('/v1/customers', customer);
}

/**
 * Обновить заказчика
 * @param {IIssue} issue Новый заказчик
 */
export function updateCustomer(customer: ICustomer) {
  return nr.put('/v1/customers', customer);
}

/**
 * Вернуть issue с указанным id
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getIssue(issueId: string): Promise<IIssue> {
  return nr.get(`/v1/issues/${issueId}`, 'issues.json', { _id: issueId }, 'object');
}

/**
 * Вернуть пользователя с указанным id
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getUser(userId: string): Promise<IUser> {
  return nr.get(`/v1/users/${userId}`, 'users.json', { _id: userId }, 'object');
}

/**
 * Обновить issue
 * @param {IIssue} issue Новый Issue
 */
export function updateIssue(issue: IIssue) {
  return nr.put('/v1/issues', issue);
}

/**
 * Обновить позицию issue
 * @param {IIssue} issue Новый Issue
 */
export function updateIssuePosition(issue: IIssue) {
  return nr.put('/v1/issues/position', issue);
}

/**
 * Создать epic в проекте
 * @param {IEpic} epic Новый epic
 * @param {string} projectId id проекта, к которому принадлежит epic
 */
export function createEpic(epic: IEpic, projectId: string) {
  return nr.post(`/v1/projects/${projectId}/epic`, epic);
}

/**
 * Создать sprint в проекте
 * @param {IEpic} sprint Новый sprint
 * @param {string} projectId id проекта, к которому принадлежит epic
 */
export function createSprint(sprint: ISprint, projectId: string) {
  return nr.post(`/v1/projects/${projectId}/sprint`, sprint);
}

/**
 * Получить sprint из проекта по его _id
 * @param {IEpic} sprint Новый sprint
 * @param {string} projectId id проекта, к которому принадлежит epic
 */
export function getSprintInProject(projectId: string, sprintId: string): Promise<ISprint> {
  return nr.get(`/v1/projects/${projectId}/sprint/${sprintId}`, 'sprints.json', { _id: sprintId }, 'object');
}

/**
 * Запустить sprint из проекта по его _id
 * @param {IEpic} sprint Новый sprint
 * @param {string} projectId id проекта, к которому принадлежит epic
 */
export function startSprintInProject(projectId: string, sprint: ISprint): Promise<ISprint> {
  return nr.put(`/v1/projects/${projectId}/sprint/start`, sprint);
}

/**
 * Создать issue
 * @param {IEpic} issue Новый issue
 */
export function createIssue(issue: IIssue) {
  return nr.post('/v1/issues', issue);
}

/**
 * Удалить вложение issue
 */
export function deleteIssue(issueId: string): Promise<null> {
  return nr.delete(`/v1/issues/${issueId}`);
}

/**
 * Скачать вложение из issue
 */
export function getIssueAttachment(issueId: string, attachmentId: string): null {
  window.open(`${process.env.VUE_APP_API_GATEWAY}/v1/issues/${issueId}/attachments/${attachmentId}`);
  return null;
}

/**
 * Добавить вложение в issue
 */
export function addIssueAttachments(issueId: string, form: FormData): Promise<IAttachment[]> {
  return nr.put(`/v1/issues/${issueId}/attachments`, form);
}

/**
 * Удалить вложение из issue
 */
export function deleteIssueAttachment(issueId: string, attachmentId: string): Promise<null> {
  return nr.delete(`/v1/issues/${issueId}/attachments/${attachmentId}`);
}

/**
 * Скачать вложение из epic
 */
export function getEpicAttachment(projectId: string, epicId: string, attachmentId: string): null {
  window.open(
    `${process.env.VUE_APP_API_GATEWAY}/v1/projects/${projectId}/epics/${epicId}/attachments/${attachmentId}`,
  );
  return null;
}

/**
 * Добавить вложение в эпик проекта
 */
export function addEpicAttachments(projectId: string, epicId: string, form: FormData): Promise<IEpic> {
  return nr.put(`/v1/projects/${projectId}/epics/${epicId}/attachments`, form);
}

/**
 * Удалить вложение из эпика в проекте
 */
export function deleteEpicAttachment(projectId: string, epicId: string, attachmentId: string): Promise<null> {
  return nr.delete(`/v1/projects/${projectId}/epics/${epicId}/attachments/${attachmentId}`);
}

/**
 * Создать комментарий в проекте
 * @param issueId
 * @param body Текст нового комментария
 */
export function createComment(issueId: string, body: { value: string }): Promise<IComment[]> {
  return nr.post(`/v1/issues/${issueId}/comments`, body);
}

/**
 * Вернуть epic с указанным id из проекта
 * @param {string} projectId id проекта, к которому принадлежат спринты
 * @param  {object} query query объект
 */
export function getEpic(projectId: string, epicId: string): Promise<IEpic> {
  return nr.get(`/v1/projects/${projectId}/epic/${epicId}`, 'epics.json', { _id: epicId }, 'object');
}

/**
 * Обновить epic в проекте
 * @param {IEpic} issue Новый Epic
 * @param {string} projectId id проекта, которому принадлежит epic
 */
export function updateEpic(epic: IEpic, projectId: string) {
  return nr.put(`/v1/projects/${projectId}/epic`, epic);
}

/**
 * Вернуть logwork с указанным id из issue
 * @param {string} issueId id issue, к которому принадлежат логворки
 * @param {string} logworkId id logwork
 * @param  {object} query query объект
 */
export function getLogwork(issueId: string, logworkId: string): Promise<ILogwork> {
  return nr.get(`/v1/issues/${issueId}/logwork/${logworkId}`, 'logworks.json', { _id: logworkId }, 'object');
}

/**
 * Удалить логворк из issue
 */
export function deleteIssueLogwork(issueId: string, logworkId: string): Promise<null> {
  return nr.delete(`/v1/issues/${issueId}/logworks/${logworkId}`);
}

/**
 * Вернуть все логворки, которые принадлежат мне за указанный год и в нужном месяце
 * @param query query объект
 */
export function getMyLogworks(query: {
  month: number;
  year: number;
}): Promise<{ projectGroup: ILogworkWithProject[]; dateGroup: ILogworkData[] }> {
  return nr.get(`/v1/issues/my_logworks?${queryString(query)}`, 'my_logworks.json', query);
}

/**
 * Вернуть все логворки, которые принадлежат пользователю за указанный год и в нужном месяце
 * @param query query объект
 */
export function getUserLogworks(query: {
  month: number;
  year: number;
  user_id: string;
}): Promise<{ projectGroup: ILogworkWithProject[]; dateGroup: ILogworkData[] }> {
  return nr.get(`/v1/issues/user_logworks?${queryString(query)}`, 'user_logworks.json', query);
}

/**
 * Вернуть минуты списаний за месяц и год пользователей
 * @param query query объект
 */
export function getAmountsByMember(query: { month: number; year: number }): Promise<ILogworkAmountMember[]> {
  return nr.get(`/v1/issues/amounts?${queryString(query)}`, 'amounts.json', query);
}

/**
 * Создать logwork в issue
 * @param {IEpic} logwork Новый logwork
 * @param {string} issueId id issue, к которому принадлежит logwork
 */
export function createLogwork(logwork: ILogwork, issueId: string): Promise<ILogwork[]> {
  return nr.post(`/v1/issues/${issueId}/logwork`, logwork);
}

/**
 * Обновить logwork в issue
 * @param {IEpic} logwork Новый logwork
 * @param {string} issueId id issue, к которому принадлежит logwork
 */
export function updateLogwork(logwork: ILogwork, issueId: string): Promise<ILogwork[]> {
  return nr.put(`/v1/issues/${issueId}/logwork`, logwork);
}

/**
 * Вернуть всех заказчиков
 * @param  {object} query query объект
 */
export function getAllCustomers(query?: { [P in keyof ICustomer]?: ICustomer[P] }): Promise<ICustomer[]> {
  return nr.get(`/v1/customers?${queryString(query)}`, 'customers.json', query);
}

/**
 * Вернуть все спринты проекта
 * @param {string} projectId id проекта, к которому принадлежит спринт
 * @param  {object} query query объект
 */
export function getAllSprints(projectId: string, query?: object): Promise<ISprint[]> {
  return nr.get(`/v1/projects/${projectId}/sprints`, 'sprints.json', query);
}

/**
 * Вернуть все эпики проекта
 * @param {string} projectId id проекта, к которому принадлежит эпик
 * @param  {object} query query объект
 */
export function getAllEpics(projectId: string, query?: object): Promise<IEpic[]> {
  return nr.get(`/v1/projects/${projectId}/epics`, 'epics.json', query);
}

/**
 * Вернуть все issue types
 * @param  {object} query query объект
 */
export function getAllIssueTypes(query?: object): Promise<IIssueType[]> {
  return nr.get('/v1/issue_types', 'issue_types.json', query);
}

/**
 * Вернуть избранные проекты пользователя
 */
export function getFavoriteProjects(): Promise<string[]> {
  return nr.get('/v1/users/favorite_projects', 'favorite_projects.json');
}

/**
 * Добавить или удалить из избранного проект пользователя
 */
export function toogleFavoriteProject(projectId: string, body: { checked: boolean }): Promise<null> {
  return nr.put(`/v1/users/favorite_projects/${projectId}`, body);
}
